import { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";

import {
  setTemporaryPassword,
  clearTemporaryPassword,
} from "../usersSlice";

type Props = {
  open: boolean;
  onClose: () => void;
  user?: any;
};

// Hands an admin a password to give someone who cannot get in — the reset
// email not arriving, or no way to read it.
//
// The password the API returns is shown here and kept nowhere else: the record
// holds only its hash, so if the admin loses it before passing it on, the way
// back is to set another one. The account is flagged, so the person is asked for
// a password of their own as soon as they sign in with this.
const SetTemporaryPasswordDialog = ({ open, onClose, user }: Props) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { temporaryPassword, temporaryPasswordStatus, temporaryPasswordError } =
    useAppSelector((state) => state.users);

  const [chosen, setChosen] = useState("");
  const [copied, setCopied] = useState(false);

  const busy = temporaryPasswordStatus === "loading";
  const email = user?.email ?? "";
  // The drawer builds the user it shows from the detail response, so this is
  // only ever empty if the record itself came back without one — better a
  // button that says so than a request the API has to refuse.
  const userId = user?._id || user?.id || "";

  const handleClose = () => {
    dispatch(clearTemporaryPassword());
    setChosen("");
    setCopied(false);
    onClose();
  };

  const handleSet = async () => {
    const result: any = await dispatch(
      setTemporaryPassword({
        userId,
        email,
        password: chosen.trim() || undefined,
      }),
    );
    if (result.error) return;
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(temporaryPassword);
      setCopied(true);
      enqueueSnackbar("Temporary password copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy the password", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set a temporary password</DialogTitle>
      <DialogContent>
        {temporaryPassword ? (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Give this to {email || "the user"}. It is shown once — it is not
              stored, and cannot be read back.
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                p: 2,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 0,
                fontFamily: "monospace",
                fontSize: 18,
                wordBreak: "break-all",
              }}
            >
              <span>{temporaryPassword}</span>
            </Box>
            <Alert severity="info">
              They will be asked to choose a password of their own the moment
              they sign in with it, and any session they had is signed out.
            </Alert>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleCopy}>
                {copied ? "Copied" : "Copy password"}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This gives {email || "the user"} a password to sign in with, for
              when the reset email does not arrive. They are asked to choose
              their own as soon as they use it.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Password (leave empty to generate one)"
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
              disabled={busy}
              helperText="At least 8 characters if you type one here."
            />
            {temporaryPasswordError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {temporaryPasswordError}
              </Alert>
            )}
            {!userId && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This user came back from the API without an id, so a password
                cannot be set here. Close the drawer, reopen the user, and try
                again.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{temporaryPassword ? "Done" : "Cancel"}</Button>
        {!temporaryPassword && (
          <Button
            variant="contained"
            onClick={handleSet}
            disabled={busy || !userId}
            startIcon={busy ? <CircularProgress size={20} /> : undefined}
          >
            Set password
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SetTemporaryPasswordDialog;
